import { createContext, useEffect, useState } from 'react';
import AuthAxios from '../axios';
import { SubscriptionInfo, SubscriptionUserInfo, User } from '../types';
import { getItem, removeItem, setItem, USERINFO, USERTOKEN } from '../utils/localStorage';
import { useNavigate } from 'react-router-dom';

type AuthState = {
  isAuthentication: boolean;
  user: User | null;
};
export type AuthContextType = AuthState & {
  setLoginSuccess: (user: User) => void;
  setLogout: () => void;
  updateUserNickName: (nickName: string) => void;
  handleSubscription: () => void;
  handleCancelSubscription: () => void;
  getAuth: () => Promise<void>;
};
export const AuthStateContext = createContext<AuthContextType>({
  isAuthentication: false,
  user: null,
  setLoginSuccess: () => {}, // 기본 구현 제공
  setLogout: () => {},
  updateUserNickName: () => {},
  handleSubscription: () => {},
  handleCancelSubscription: () => {},
  getAuth: async () => {},
});

export const AuthContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthentication: getItem(USERTOKEN) ? true : false,
    user: getItem(USERINFO),
  });

  const navigate = useNavigate();

  const setLogout = () => {
    removeItem(USERTOKEN);
    removeItem(USERINFO);
    setAuthState({ isAuthentication: false, user: null });
  };

  const setLoginSuccess = (user: User) => {
    setAuthState({ isAuthentication: true, user: user });
    setItem(USERINFO, user);
  };

  /** 닉네임 변경 — 헤더·홈 등 AuthContext user 구독 UI 동기화 */
  const updateUserNickName = (nickName: string) => {
    if (!authState.user) return;
    const user = { ...authState.user, nickName };
    setAuthState({ isAuthentication: true, user });
    setItem(USERINFO, user);
  };

  /** 구독 하기 */
  const handleSubscription = () => {
    if (authState.user) {
      setAuthState({
        isAuthentication: true,
        user: { ...authState.user, currentSubscription: true },
      });
      setItem(USERINFO, { ...authState.user, currentSubscription: true });
    }
  };

  /** 구독 취소하기. */
  const handleCancelSubscription = () => {
    if (authState.user) {
      setAuthState({
        isAuthentication: true,
        user: { ...authState.user, currentSubscription: false },
      });
      setItem(USERINFO, { ...authState.user, currentSubscription: false });
    }
  };

  const getAuth = async () => {
    if (authState.isAuthentication) {
      await AuthAxios.get<{ subscription: SubscriptionInfo; userInfo: SubscriptionUserInfo }>(
        `/users/current-subsription`
      )
        .then((resolve) => {
          if (resolve.data.subscription.endDate) {
            if (new Date() < new Date(resolve.data.subscription.endDate)) {
              setAuthState({
                isAuthentication: getItem(USERTOKEN) ? true : false,
                user: { ...getItem(USERINFO), currentSubscription: true },
              });
            } else {
              setAuthState({
                isAuthentication: getItem(USERTOKEN) ? true : false,
                user: { ...getItem(USERINFO), currentSubscription: false },
              });
            }
          } else {
            setAuthState({
              isAuthentication: getItem(USERTOKEN) ? true : false,
              user: { ...getItem(USERINFO), currentSubscription: false },
            });
          }
          if (!resolve.data.userInfo.termsAgreed) {
            navigate('/auth/agrement', { replace: true });
          }
        })
        .catch((error) => {
          setAuthState({
            isAuthentication: getItem(USERTOKEN) ? true : false,
            user: { ...getItem(USERINFO), currentSubscription: false },
          });
        });
    } else {
      setLogout();
    }
  };

  useEffect(() => {
    getAuth();
  }, [authState.isAuthentication]);

  const value = {
    ...authState,

    setLoginSuccess,
    setLogout,
    updateUserNickName,
    handleSubscription,
    handleCancelSubscription,
    getAuth,
  };

  return <AuthStateContext.Provider value={value}>{children}</AuthStateContext.Provider>;
};

// export const useAuthState = () => {
//   const authState = useContext(AuthStateContext);
//   if (!authState) throw new Error("AuthProvider not found");
//   return authState;
// };
